import { RefElement } from 'widgets/toolbox/RefElement';
import { extractPath, isMacOs } from '../utils';

interface IGTMEventData {
    url?: string | null;
    [key: string]: unknown;
}

/**
 * @param Header Base widget for extending
 * @returns CoreHeader widget
 */
function CoreHeaderClassCreator(Header: import('widgets/global/Header').THeader) {
    /**
     * @category widgets
     * @subcategory global
     * @class CoreHeader
     * @augments Header
     * @classdesc The main purpose is to define anchor for header and to toggle active class
     * @property {string} data-widget - Widget name `header`
     * @example <caption>Example of Header widget usage</caption>
     * <header
     *     class="l-header"
     *     data-widget="header"
     *     data-context="header"
     *     data-init-on-load="loadContext"
     *     aria-label="${Resource.msg('header.label','global',null)}"
     *     data-tau-unique="header"
     * >
     *      <div class="l-header-inner">
     *            ... account header inner content
     *      </div>
     * </header>
     */

    class CoreHeader extends Header {
        HEADER_HEIGHT_CSS_VAR = '--header-height';

        prefs() {
            return {
                macOsClassName: 'm-mac_os',
                ...super.prefs()
            };
        }

        /**
         * @description Initialize widget logic
         */
        init() {
            super.init();

            this.eventBus().on('viewtype.change', 'setCssVarHeaderHeight');
            this.setCssVarHeaderHeight();

            if (isMacOs()) {
                this.ref('html').addClass(this.prefs().macOsClassName);
            }
        }

        /**
         * Calculates and retrieves the height of the header element.
         * If the header element exists, its height is set as a CSS variable
         * on the HTML element for further use in styling.
         */
        setCssVarHeaderHeight() {
            const header = this.ref('self').get();
            let headerHeight = 0;

            if (header) {
                headerHeight = header.offsetHeight;
                this.ref('html').get()?.style.setProperty(this.HEADER_HEIGHT_CSS_VAR, `${headerHeight}px`);
            }
        }

        /**
         * Handles the click event on a mainline element.
         *
         * @param elem - A reference to the HTML element that triggered the event.
         */
        handleTrackClick(elem: RefElement<HTMLElement>) {
            const data: IGTMEventData = elem.data('gtm-event-data') || {};

            if (data.url !== null) {
                data.url = extractPath(elem.attr('href'));
            }

            this.handleGTMUpdates('header.click', data);
        }

        /**
         * @description Handles GTM tracking updates
         */
        handleGTMUpdates(eventName: string, data = {}) {
            this.eventBus().emit(eventName, data);
        }
    }

    return CoreHeader;
}

export type TCoreHeader = ReturnType<typeof CoreHeaderClassCreator>;

export type TCoreHeaderInstance = InstanceType<TCoreHeader>;

export default CoreHeaderClassCreator;
